<template>
  <spinner
    v-if="showModalSpinner"
    fullScreen
  />
  <DialogComponent
    v-else
    @submit="onSubmit"
    :title="this.title"
    :customClose="this.close"
  >
    <form @submit="onSubmit">
      <b-form-group
        id="fieldName"
        label="Field name:"
        label-for="fieldName"
        label-class="label-bold"
      >
        <div class="pb-1">
          <span class="hint"><i class="mdi mdi-information-outline"></i> Provide a descriptive label for this question. Example: "phone" </span>
        </div>
        <input
          id="fieldName"
          type="text"
          :class="{'is-invalid': errors.has('fieldName')}"
          v-model="question.fieldName"
          placeholder="Enter field name"
          v-validate="'required'"
          name="fieldName"
          data-vv-as="Field Name"
          class="form-control"
          autofocus
        />
        <span
          v-show="errors.has('fieldName')"
          class="error-message"
        >
          {{ errors.first('fieldName') }}
        </span>
      </b-form-group>
      <b-form-group
        id="question"
        label="Question:"
        label-for="question"
        label-class="label-bold"
      >
        <div class="pb-1">
          <span class="hint"><i class="mdi mdi-information-outline"></i> Example: "What is your phone number?" </span>
        </div>
        <input
          id="question"
          type="text"
          v-model="question.question"
          placeholder="Enter question"
          :class="{'is-invalid': errors.has('question')}"
          v-validate="'required'"
          data-vv-as="Question:"
          name="question"
          class="form-control"
        />
        <span
          v-show="errors.has('question')"
          class="error-message"
        >
          {{ errors.first('question') }}
        </span>
      </b-form-group>
      <b-form-group
        label="Field Type:"
        label-for="type"
        label-class="label-bold"
      >
        <div class="pb-1">
          <span class="hint"><i class="mdi mdi-information-outline"></i> What should be the format of the question response? Keep "Free Text" if not sure.</span>
        </div>
        <b-form-select
          v-model="question.type"
          :options="types"
          :class="{'is-invalid': errors.has('type')}"
          v-validate="'required'"
          name="type"
        />
        <span
          v-show="errors.has('type')"
          class="error-message"
        >
          {{ errors.first('type') }}
        </span>
      </b-form-group>

      <ex-choice
        v-if="showChoice"
        :type="question.type"
        v-model="question.params.options"
        :name="currentName"
        v-validate="'required'"
        class="mb-3"
      ></ex-choice>
      <div
        v-show="errors.has(currentName)"
        class="error-form"
      >
        {{ errors.first(currentName) }}
      </div>
      <div class="pb-1">
        <span class="hint"><i class="mdi mdi-information-outline"></i> Required questions must be answered for lead to be marked completed. To maximize lead completion, keep Required questions to a minimum.</span>
      </div>
      <div class="custom-control custom-checkbox pb-3">
        <input type="checkbox" class="custom-control-input" id="customCheck1" v-model="question.mandatory">
        <label class="custom-control-label" for="customCheck1"> {{(question.mandatory) ? 'Required' : 'Optional'}}</label>
      </div>
    </form>
  </DialogComponent>
</template>

<script>
import Switches from 'vue-switches';
import DialogComponent from '@/components/Dialog';
import Spinner from '@/components/Spinner';
import ExChoice from '@/modals/FieldAndQuery/components/MultipleAndExclusiveChoice';

import QUESTION from '@/graphql/Questions/Question.gql';
import CREATE_QUESTION from '@/graphql/Questions/CreateQuestion.gql';
import EDIT_QUESTION from '@/graphql/Questions/EditQuestion.gql';

import checkIsAccessDenied from '@/helpers/checkIsAccessDenied';

export default {
  name: 'edit-field-and-query-component',
  components: {
    Switches,
    DialogComponent,
    ExChoice,
    Spinner,
  },
  props: {
    id: String,
    questionId: String,
  },

  data() {
    return {
      types: ['Free text', 'Multiple Choice', 'Exclusive Choice', 'Date', 'Phone', 'Email', 'Time'],
      question: {
        fieldName: '',
        question: '',
        type: null,
        mandatory: false,
        params: {
          options: [],
        },
      },
      showModalSpinner: 0,
    };
  },
  computed: {
    title() {
      return `${this.questionId ? 'Update' : 'Create'} Field and Question`;
    },
    currentName() {
      return {
        'Free text': 'Freetext',
        'Multiple Choice': 'MultipleChoice',
        'Exclusive Choice': 'ExclusiveChoice',
        Date: 'Date',
        Phone: 'Phone',
        Email: 'Email',
        Time: 'Time',
      }[this.question.type];
    },
    showChoice() {
      return this.question.type === 'Multiple Choice' || this.question.type === 'Exclusive Choice';
    },
  },
  methods: {
    close() {
      this.$router.push({ name: 'CampaignDetails', params: { id: this.id } });
    },
    async onSubmit() {
      try {
        const valid = await this.$validator.validateAll();
        if (!valid) {
          return;
        }
      } catch (e) {
        return;
      }

      const { fieldName, question, type, mandatory, params } = this.question;

      this.$apollo
        .mutate({
          mutation: this.questionId ? EDIT_QUESTION : CREATE_QUESTION,
          variables: {
            id: parseInt(this.questionId),
            input: {
              fieldName,
              question,
              type,
              mandatory,
              campaignId: this.id,
              params: JSON.stringify(params),
            },
          },
          refetchQueries: ({ data }) => {
            const response = data.createQuestion || data.updateQuestion;
            return response.success ? ['questions'] : [];
          },
        })
        .then(() => {
          this.$notify({
            group: 'success',
            type: 'success',
            text: `Question was ${this.id ? 'edited' : 'created'}`,
          });
          this.close();
        })
        .catch(e => {
          const isAccessDenied = checkIsAccessDenied(e);

          if (e.message.includes('ER_DUP_ENTRY')) {
            return this.$validator.errors.add('fieldName', 'This Field Name already exists');
          }

          if (isAccessDenied) {
            return this.$notify({
              group: 'error',
              type: 'error',
              text: 'Access denied',
            });
          }

          this.$notify({
            group: 'error',
            type: 'error',
            text: e.message,
          });
        });
    },
  },
  apollo: {
    question: {
      query: QUESTION,
      loadingKey: 'showModalSpinner',
      fetchPolicy: 'network-only',
      variables() {
        return {
          id: +this.questionId,
        };
      },
      result({ data, loading }) {
        if (!loading) {
          this.question = {
            ...data.question,
            params: JSON.parse(data.question.params),
          };
        }
      },
      skip() {
        return !this.questionId;
      },
      error(error) {
        const isAccessDenied = checkIsAccessDenied(error);

        if (isAccessDenied) {
          this.$router.push({ name: 'NotFound' });
        }
      },
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
  /*.checkboxes-wrapper {*/
    /*max-height: 378px;*/
    /*overflow: auto;*/

    /*.form-check {*/
      /*border-top: 1px solid #c1c1c1;*/
      /*padding-top: 0.4rem;*/
      /*margin-bottom: 0.4rem;*/
    /*}*/
  /*}*/
  .error-form {
    margin-bottom: 5px;
    color: #f00;
  }
  .hint {
    font-weight: 300;
    color: #90a4ae;
  }
</style>



// WEBPACK FOOTER //
// src/modals/FieldAndQuery/EditFieldAndQueryModal.vue